import React from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError, HelpText } from '../../components';
import { formatRequiredLabel } from '../../util/uiHelpers';

import css from './FieldSelect.module.css';

const FieldSelectComponent = props => {
  const {
    rootClassName,
    className,
    selectClassName,
    labelClassName,
    id,
    label,
    helpText,
    input,
    meta,
    children,
    onChange,
    showLabelAsDisabled,
    isRequired,
    ...rest
  } = props;

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error, submitFailed } = meta;

  // Error message and input error styles are shown after touch or a failed submit.
  const hasError = (touched || submitFailed) && invalid && error;

  const selectClasses = classNames({
    [selectClassName]: selectClassName,
    [css.selectError]: hasError,
  });
  const handleChange = e => {
    input.onChange(e);
    if (onChange) {
      onChange(e.currentTarget.value);
    }
  };

  const selectProps = {
    className: selectClasses,
    id,
    ...input,
    onChange: handleChange,
    ...(hasError ? { 'aria-invalid': true } : {}),
    ...rest,
  };

  const labelClasses = classNames({
    [css.labelDisabled]: showLabelAsDisabled,
    [labelClassName]: !!labelClassName,
  });

  const displayLabel = formatRequiredLabel(label, isRequired);
  const classes = classNames(rootClassName || css.root, className);
  return (
    <div className={classes}>
      {displayLabel ? (
        <label htmlFor={id} className={labelClasses}>
          {displayLabel}
        </label>
      ) : null}
      <select {...selectProps}>{children}</select>
      <HelpText helpText={helpText} />
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

/**
 * Final Form Field wrapping <select> input
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.selectClassName add more style rules to <select> component
 * @param {string} props.name Name of the input in Final Form
 * @param {string} props.id Label is optional, but if it is given, an id is also required so the label can reference the input in the `for` attribute
 * @param {ReactNode} props.label
 * @param {ReactNode} props.children
 * @param {boolean} props.disabled Whether the select element is disabled
 * @param {boolean} props.showLabelAsDisabled Whether the label is disabled
 * @returns {JSX.Element} Final Form Field containing <select> input
 */
const FieldSelect = props => {
  return <Field component={FieldSelectComponent} {...props} />;
};

export default FieldSelect;
